import CommentIcon from "@mui/icons-material/Comment";
import Badge from "@mui/material/Badge";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { keyframes } from "@mui/material";
import { styled } from "@mui/material/styles";
import CommentThread from "work/proposals/comments/threads/comment-thread";
import ProposalField from "work/proposals/proposal-fields/proposal-field";

const pulse = keyframes`
  0% {
    transform: scale(0.95);
    opacity: 0.85;
  }
  50% {
    transform: scale(1.1);
    opacity: 1.0;
  }
  100% {
    transform: scale(0.95);
    opacity: 0.85;
  }
`;
const HighlightIconButton = styled(IconButton, { shouldForwardProp: (prop) => prop !== "active" })<{
  active?: boolean;
}>(({ active }) => ({
  position: "relative",
  zIndex: 0,
  ...(active && {
    animation: `${pulse} 1.5s ease-in-out infinite`,
    "&::after": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(21, 101, 192, 0.05)",
      borderRadius: "50%",
      animation: `${pulse} 1.5s ease-in-out infinite`,
      zIndex: -1,
    },
  }),
}));

type CommentsButtonProps = {
  commentThreads?: CommentThread[];
  field: ProposalField;
  toolTip: string;
  disabled?: boolean;
  onCommentsClicked?: () => void;
  hideUnreadCount?: boolean;
  isActive?: boolean;
};

export default function CommentsButton(props: Readonly<CommentsButtonProps>) {
  const { commentThreads, field, toolTip, onCommentsClicked, hideUnreadCount, isActive, disabled } = props;

  const unreadCount = field
    ? commentThreads
        ?.filter((thread) => thread.field.isEqualTo(field))
        .reduce((acc, thread) => acc + (thread.numUnreadMessages ?? 0), 0)
    : 0;

  const hasComments = commentThreads?.some((thread) => thread.field.isEqualTo(field)) ?? false;

  const getBadgeVariant = () => {
    if (hideUnreadCount) {
      return "dot";
    }
    return unreadCount ? "standard" : "dot";
  };

  const getBadgeContent = () => {
    if (hideUnreadCount) {
      return undefined;
    }
    return unreadCount === 0 ? undefined : unreadCount;
  };

  return (
    <Tooltip title={toolTip}>
      <span>
        <HighlightIconButton
          disabled={disabled || !onCommentsClicked || (hideUnreadCount && !hasComments)}
          active={isActive && Boolean(unreadCount)}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onCommentsClicked?.();
          }}
        >
          <Badge
            variant={getBadgeVariant()}
            color="secondary"
            overlap="circular"
            badgeContent={getBadgeContent()}
            invisible={!commentThreads?.some((thread) => thread.field.isEqualTo(field))}
          >
            <CommentIcon fontSize="medium" />
          </Badge>
        </HighlightIconButton>
      </span>
    </Tooltip>
  );
}
